import { createLDReactProviderWithClient } from '@launchdarkly/react-sdk';
import type { LDContext } from '@launchdarkly/react-sdk';
import { getLaunchDarklyBootstrap, getLaunchDarklyClientId } from '../metaTags';
import { createOverridableLDClient } from './overridableClient';

export type { OverridableLDClient } from './overridableClient';
export {
  createOverridableLDClient,
  wrapWithOverrides,
} from './overridableClient';

// Flags come from bootstrap, so the SDK's best-effort network warnings
// shouldn't reach the user's console — silence them with a no-op logger.
const noop = () => undefined;
const SILENT_LOGGER = { error: noop, warn: noop, info: noop, debug: noop };

// Shared fixed anonymous context → ~1 MAU per environment (free-tier posture).
const SHARED_CONTEXT: LDContext = {
  kind: 'user',
  key: 'anonymous',
  anonymous: true,
};

/**
 * Creates the LaunchDarkly provider for a portal app — the single place the
 * frontend wires up LaunchDarkly. Call once at the app root and mount the
 * returned provider above the tree; components below it read flags through the
 * LD hooks (e.g. `useBoolVariation`) with no per-app setup.
 *
 * @remarks
 * Flags are evaluated server-side by `portal_renderer` and injected into the
 * page, then handed to the client as `bootstrap`. With the live stream disabled,
 * the browser resolves flags with no streaming/eval call to LaunchDarkly, so
 * agency (ECC) networks don't need to whitelist `launchdarkly.com` for flag
 * delivery. The client is wrapped via {@link createOverridableLDClient} so the
 * debug panel can override flags locally, and it falls back to flag defaults
 * when the bootstrap is empty.
 *
 * This is the one seam for changing flag delivery: to route a live connection
 * (and analytics events) through our own domain — reverse proxy, then Relay
 * Proxy — set the client's `serviceEndpoints` (stream + events URLs) and
 * re-enable streaming here. No consuming app changes.
 *
 * @returns A React provider component to mount at the app root; it exposes the
 *   LaunchDarkly context to the hooks used anywhere below it.
 *
 * @example
 * ```tsx
 * // apps/<app>/src/index.js — mount once at the root
 * import { createLaunchDarklyProvider } from '@rsos/utils/launchDarkly';
 *
 * const LDProvider = createLaunchDarklyProvider();
 *
 * root.render(
 *   <LDProvider>
 *     <App />
 *   </LDProvider>,
 * );
 * ```
 */
export const createLaunchDarklyProvider = () => {
  const clientSideId = getLaunchDarklyClientId() || 'local-development';
  const client = createOverridableLDClient(clientSideId, SHARED_CONTEXT, {
    streaming: false,
    // Not using Experimentation — skip the per-page-load goals GET to LD.
    fetchGoals: false,
    logger: SILENT_LOGGER,
  });
  client.start({ bootstrap: getLaunchDarklyBootstrap() });
  return createLDReactProviderWithClient(client);
};
